import { useCallback, useRef, useState } from "react";
import api from "../../services/api";

/**
 * Hook de auto-salvamento com debounce.
 *
 * Uso:
 *   const { triggerSave, saveStatus } = useAutoSave(flowId);
 *   // Chamar triggerSave(nodes, edges) a cada mudanca.
 *   // saveStatus: "idle" | "saving" | "saved" | "error"
 */
// Remove campos internos do editor (highlight, simulacao) antes de salvar
function cleanNodesForSave(nodes) {
  return nodes.map((n) => {
    const { _highlight, _simStatus, ...cleanData } = n.data || {};
    return { ...n, data: cleanData };
  });
}

export default function useAutoSave(flowId, delay = 5000) {
  const [saveStatus, setSaveStatus] = useState("idle");
  const timerRef = useRef(null);
  const lastSavedRef = useRef(null);

  const triggerSave = useCallback(
    (nodes, edges) => {
      if (!flowId) return;
      setSaveStatus("pending");

      if (timerRef.current) clearTimeout(timerRef.current);

      timerRef.current = setTimeout(async () => {
        const cleanNodes = cleanNodesForSave(nodes);
        const payload = JSON.stringify({ nodes: cleanNodes, connections: edges });
        if (payload === lastSavedRef.current) {
          setSaveStatus("saved");
          return;
        }
        try {
          setSaveStatus("saving");
          await api.post("/flowbuilder/flow", {
            idFlow: flowId,
            nodes: cleanNodes,
            connections: edges,
          });
          lastSavedRef.current = payload;
          setSaveStatus("saved");
        } catch (err) {
          console.error("[AutoSave] error:", err);
          setSaveStatus("error");
        }
      }, delay);
    },
    [flowId, delay]
  );

  const forceSave = useCallback(
    async (nodes, edges) => {
      if (!flowId) return;
      if (timerRef.current) clearTimeout(timerRef.current);
      try {
        setSaveStatus("saving");
        const cleanNodes = cleanNodesForSave(nodes);
        await api.post("/flowbuilder/flow", {
          idFlow: flowId,
          nodes: cleanNodes,
          connections: edges,
        });
        lastSavedRef.current = JSON.stringify({ nodes: cleanNodes, connections: edges });
        setSaveStatus("saved");
      } catch (err) {
        console.error("[AutoSave] force save error:", err);
        setSaveStatus("error");
      }
    },
    [flowId]
  );

  return { triggerSave, forceSave, saveStatus };
}
